/**
 * Files API Service (v2)
 *
 * DB-tracked file management for site images and documents
 */

import { api } from '../client';
import apiClient from '../client';

export interface FileRecord {
  id: string;
  original_filename: string;
  mime_type: string;
  size_bytes: number;
  category: 'document' | 'site_image';
  installation_id: string | null;
  project_id: string | null;
  uploaded_by_id: string;
  created_at: string;
}

export const filesService = {
  /**
   * List site images for an installation
   */
  async listByInstallation(installationId: string): Promise<FileRecord[]> {
    return await api.get(`/api/v2/files/installation/${installationId}`);
  },

  /**
   * List documents for a project
   */
  async listByProject(projectId: string): Promise<FileRecord[]> {
    return await api.get(`/api/v2/files/project/${projectId}`);
  },

  /**
   * Download a file as blob
   */
  async download(fileId: string): Promise<Blob> {
    const response = await apiClient.get(`/api/v2/files/${fileId}`, {
      responseType: 'blob',
    });
    return response.data;
  },

  /**
   * Get download URL for a file (for <img src> and <a href>)
   */
  getDownloadUrl(fileId: string): string {
    return `/api/v2/files/${fileId}`;
  },

  /**
   * Upload a site image to an installation
   */
  async uploadToInstallation(installationId: string, file: File): Promise<FileRecord> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(
      `/api/v2/files/installation/${installationId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Upload a document to a project
   */
  async uploadToProject(projectId: string, file: File): Promise<FileRecord> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post(`/api/v2/files/project/${projectId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  /**
   * Delete a file
   */
  async delete(fileId: string): Promise<void> {
    await api.delete(`/api/v2/files/${fileId}`);
  },
};
